import {
  useConnectWithOtp,
  isAuthenticatedWithAWallet,
} from "@dynamic-labs/sdk-react-core";

import {
  zendeskLogin,
  redirectToZendesk,
  checkInternetAndShowAlert,
  getLandingPageRoute,
} from "../../../common/utils/common.fn";
import {
  AnalyticsEvents,
  capturePosthogEvent,
} from "../../../common/utils/analytics";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../redux/store";
import { showAlert } from "../../../common/utils";
import { OtpFormValues } from "../services/types";
import { getCmsLinks } from "../../../common/utils";
import { setWalletDetails } from "../services/action";
import { Env, Paths } from "../../../common/constants";
import { setLoading } from "../../../redux/global.slice";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { setIsLogin, setUserEmail } from "../services/login.slice";

const useOTPHelper = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const OTP_RESEND_INTERVAL = 60;
  const [timer, setTimer] = useState(0);
  const [OTPCount, setOTPCount] = useState(0);

  const { userId } = useAppSelector((state: RootState) => state.verifyEmail);
  const { email, isLogin } = useAppSelector((state: RootState) => state.login);

  const { cmsLinks } = useAppSelector(
    (state: RootState) => state.globalcontent,
  );

  const { verifyOneTimePassword, connectWithEmail } = useConnectWithOtp();

  const brandId = localStorage.getItem("brandId");

  useEffect(() => {
    if (!isLogin) navigate(Paths.LOGIN);
    const storedExpiryTime = localStorage.getItem("otpExpiryTime");
    if (storedExpiryTime) {
      const expiryTime = parseInt(storedExpiryTime, 10);
      const timeRemaining = Math.max(expiryTime - Date.now(), 0);
      setTimer(Math.ceil(timeRemaining / 1000));
    }

    return () => {
      setTimer(0);
      localStorage.removeItem("otpExpiryTime");
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    let countdown: NodeJS.Timeout;
    if (timer > 0) {
      countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }

    return () => clearInterval(countdown);
  }, [timer]);

  const handleRedirect = (
    userType: string,
    permissions: any,
    isSubscription: boolean,
  ) => {
    const landingPage = getLandingPageRoute(
      userType,
      permissions,
      isSubscription,
    );
    navigate(landingPage);

    dispatch(setIsLogin(false));
    showAlert(1, "You have been logged in Successfully");
  };

  const onSubmitOtpHandler = async (
    values: OtpFormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    if (OTPCount > 4) {
      showAlert(2, "You have exceeded the maximum attempts.");
      dispatch(setUserEmail(""));
      navigate(Paths.LOGIN);
      return;
    } else if (!checkInternetAndShowAlert(showAlert)) {
      dispatch(setLoading(false));
      return;
    } else {
      setSubmitting(true);
      dispatch(setLoading(true));
      setOTPCount((prevCount: number) => prevCount + 1);
      try {
        if (brandId) {
          capturePosthogEvent(AnalyticsEvents.HELP_CENTER_OPENED, {
            user_id: userId,
            source_page: "/login",
          });
          dispatch(
            zendeskLogin(email, async (token) => {
              await verifyOneTimePassword(values.otp)
                .then(async (response) => {
                  capturePosthogEvent(AnalyticsEvents.OTP_CONFIRMED);
                  const user = response && response.user;

                  const authenticatedWithAWallet =
                    //@ts-ignore
                    isAuthenticatedWithAWallet(user);

                  const dataToSend = {
                    isAuthenticatedWithAWallet: authenticatedWithAWallet,
                    manufacturerUserId: user?.id,
                    email: user?.email,
                    verifiedCredentials: {
                      walletAddress: user?.verifiedCredentials[0].address,
                      chain: user?.verifiedCredentials[0].chain,
                      id: user?.verifiedCredentials[0].id,
                      walletName: user?.verifiedCredentials[0].walletName,
                      walletProvider:
                        user?.verifiedCredentials[0].walletProvider,
                    },
                  };

                  dispatch(setWalletDetails(dataToSend, userId));
                  capturePosthogEvent(AnalyticsEvents.MANUFACTURER_LOGIN, {
                    user_id: userId,
                    method: "SSO",
                  });
                  dispatch(redirectToZendesk(token, Env.BRAND_SUPPORT_URL));
                  localStorage.removeItem("brandId");
                })
                .catch((error) => {
                  console.error({ error });
                  dispatch(setLoading(false));
                  capturePosthogEvent(
                    AnalyticsEvents.MANUFACTURER_LOGIN_FAILED,
                    {
                      email,
                      reason: "invalid_otp",
                    },
                  );
                  showAlert(
                    2,
                    "Invalid OTP. Please enter a valid 6-digit code",
                  );
                });
            }),
          );
        } else {
          await verifyOneTimePassword(values.otp)
            .then(async (response) => {
              capturePosthogEvent(AnalyticsEvents.OTP_CONFIRMED);
              const user = response && response.user;

              //@ts-ignore
              const authenticatedWithAWallet = isAuthenticatedWithAWallet(user);

              const dataToSend = {
                isAuthenticatedWithAWallet: authenticatedWithAWallet,
                manufacturerUserId: user?.id,
                email: user?.email,
                verifiedCredentials: {
                  walletAddress: user?.verifiedCredentials[0].address,
                  chain: user?.verifiedCredentials[0].chain,
                  id: user?.verifiedCredentials[0].id,
                  walletName: user?.verifiedCredentials[0].walletName,
                  walletProvider: user?.verifiedCredentials[0].walletProvider,
                },
              };

              dispatch(setWalletDetails(dataToSend, userId, handleRedirect));
              capturePosthogEvent(AnalyticsEvents.MANUFACTURER_LOGIN, {
                user_id: userId,
                method: "email",
              });
            })
            .catch((error) => {
              console.error({ error });
              dispatch(setLoading(false));
              capturePosthogEvent(AnalyticsEvents.MANUFACTURER_LOGIN_FAILED, {
                email,
                reason: "invalid_otp",
              });
              showAlert(2, "Invalid OTP. Please enter a valid 6-digit code");
            });
        }
      } catch (error) {
        dispatch(setLoading(false));
        console.error("Error verifying OTP:", error);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const onSubmitEmailHandler = async (email: string) => {
    dispatch(setLoading(true));
    try {
      await connectWithEmail(email)
        .then(() => {
          dispatch(setLoading(false));
          navigate(Paths.OTP, { state: { email: email } });
          const newExpiryTime = Date.now() + OTP_RESEND_INTERVAL * 1000;
          localStorage.setItem("otpExpiryTime", newExpiryTime.toString());
          setTimer(OTP_RESEND_INTERVAL);
          showAlert(1, "Verification Code has been resent successfully");
        })
        .catch((error) => {
          dispatch(setLoading(false));
          if (error?.code?.includes("too_many_email_verification_attempts")) {
            showAlert(2, "You have exceeded the maximum attempts.");
            navigate(Paths.LOGIN);
            dispatch(setUserEmail(""));
          } else {
            if (error && error?.code) showAlert(2, error?.code);
            else showAlert(2);
          }
          throw new Error("Error connecting with email:", error);
        });
    } catch (error) {
      dispatch(setLoading(false));
      console.error("Error connecting with email:", error);
    }
  };

  const handleOtpChange = (otpValue: any, setFieldValue: any) => {
    setFieldValue("otp", otpValue);
  };

  const handleResendCode = () => {
    onSubmitEmailHandler(email);
  };

  const handleSupportClick = () => {
    navigate(Paths.SUPPORT, { state: { email: email } });
  };

  const handleSupportBackBtnClick = () => {
    navigate(-1);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    dispatch(getCmsLinks());
    //eslint-disable-next-line
  }, [dispatch]);
  return {
    // otp,
    email,
    timer,
    isLogin,
    cmsLinks,

    formatTime,
    handleOtpChange,
    handleResendCode,
    onSubmitOtpHandler,
    handleSupportClick,
    handleSupportBackBtnClick,
  };
};

export default useOTPHelper;
